import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { initiateNylasAuth, disconnectNylas } from '../lib/nylasAuth';
import { invalidateCache, setCacheUserId } from '../lib/nylasCache';
import type { NylasGrant } from '../types/nylas';

interface NylasAuthState {
  grant: NylasGrant | null;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  initialize: () => Promise<void>;
  connect: (redirectPath?: string) => Promise<void>;
  disconnect: () => Promise<void>;
  refreshGrant: () => Promise<void>;
  reset: () => void;
  isConnected: () => boolean;
  getEmail: () => string | null;
}

export const useNylasAuthStore = create<NylasAuthState>((set, get) => ({
  grant: null,
  isInitialized: false,
  isLoading: false,
  error: null,

  initialize: async () => {
    if (get().isInitialized) return;

    set({ isLoading: true });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCacheUserId(null);
        set({ isInitialized: true, isLoading: false });
        return;
      }

      setCacheUserId(user.id);

      const { data: grant } = await supabase
        .from('nylas_grants')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('connected_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      set({ grant, isInitialized: true, isLoading: false, error: null });
    } catch (err: any) {
      console.error('Error initializing Nylas auth:', err);
      set({ isInitialized: true, isLoading: false, error: err.message });
    }
  },

  connect: async (redirectPath?: string) => {
    set({ isLoading: true, error: null });
    try {
      await initiateNylasAuth(redirectPath);
    } catch (err: any) {
      set({ isLoading: false, error: err.message });
    }
  },

  disconnect: async () => {
    const { grant } = get();
    if (!grant) return;

    set({ isLoading: true, error: null });
    try {
      await disconnectNylas(grant.grant_id);
      set({ grant: null, isLoading: false });
    } catch (err: any) {
      set({ isLoading: false, error: err.message });
    }
  },

  refreshGrant: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: grant } = await supabase
        .from('nylas_grants')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('connected_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      set({ grant, error: null });
    } catch (err: any) {
      console.error('Error refreshing Nylas grant:', err);
    }
  },

  reset: () => {
    setCacheUserId(null);
    invalidateCache();
    set({ grant: null, isInitialized: false, isLoading: false, error: null });
  },

  isConnected: () => {
    const { grant } = get();
    return !!grant && grant.status === 'active';
  },

  getEmail: () => {
    const { grant } = get();
    return grant?.email || null;
  },
}));
